import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useProduct } from '@/hooks/useProducts';
import { productsApi } from '@/services/products';
import { materialsApi } from '@/services/materials';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ProductCheckoutPanel } from '@/components/checkout/ProductCheckoutPanel';
import { trackViewItem } from '@/services/analytics';

const ModelViewer = lazy(() =>
  import('@/components/upload/ModelViewer').then((m) => ({ default: m.ModelViewer })),
);

export const ProductDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { data: product, isLoading, error } = useProduct(id ?? '');
  const trackedId = useRef<string | null>(null);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);

  // Fetch materials for color selection
  const { data: allMaterials } = useQuery({
    queryKey: ['materials'],
    queryFn: () => materialsApi.list(),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch presigned download URL for the product's 3MF file
  const has3dFile = !!product?.fileKey;
  const { data: modelUrl } = useQuery({
    queryKey: ['product-file-url', product?.productId],
    queryFn: () => productsApi.getFileDownloadUrl(product!.productId),
    enabled: has3dFile,
    staleTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    if (!product || trackedId.current === product.productId) return;
    trackedId.current = product.productId;
    trackViewItem({
      item_id: product.productId,
      item_name: product.name,
      item_category: product.category,
      price: product.basePrice,
    });
  }, [product]);

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div
        className="container-main text-center"
        style={{ paddingTop: 'var(--space-12)', paddingBottom: 'var(--space-12)' }}
      >
        <h2
          style={{
            fontSize: 'var(--text-2xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--text-primary)',
          }}
        >
          Product Not Found
        </h2>
        <p style={{ marginTop: 'var(--space-2)', color: 'var(--text-secondary)' }}>
          This product may no longer be available.
        </p>
        <Link
          to="/catalog"
          style={{
            marginTop: 'var(--space-4)',
            display: 'inline-block',
            color: 'var(--text-accent)',
            textDecoration: 'none',
          }}
        >
          Back to Catalog
        </Link>
      </div>
    );
  }

  return (
    <div
      className="container-main"
      style={{
        maxWidth: '1100px',
        paddingTop: 'var(--space-12)',
        paddingBottom: 'var(--space-12)',
      }}
    >
      <Link
        to="/catalog"
        style={{
          fontSize: 'var(--text-sm)',
          color: 'var(--text-accent)',
          textDecoration: 'none',
        }}
      >
        &larr; Back to Catalog
      </Link>

      <div className="quote-layout">
        {/* Left column -- product info + 3D preview */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-8)' }}>
          {/* Product info bar */}
          <div
            className="flex flex-col sm:flex-row sm:items-center justify-between"
            style={{
              border: '1px solid var(--border-default)',
              backgroundColor: 'var(--bg-surface)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-3) var(--space-4)',
              gap: 'var(--space-3)',
            }}
          >
            <div className="flex items-center" style={{ gap: 'var(--space-3)', minWidth: 0 }}>
              <div
                className="flex items-center justify-center"
                style={{
                  height: '2.5rem',
                  width: '2.5rem',
                  borderRadius: 'var(--radius-lg)',
                  backgroundColor: 'var(--color-primary-50)',
                  color: 'var(--color-primary-700)',
                  flexShrink: 0,
                }}
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
                  <polyline strokeLinecap="round" strokeLinejoin="round" points="3.27 6.96 12 12.01 20.73 6.96" />
                  <line x1="12" y1="22.08" x2="12" y2="12" />
                </svg>
              </div>
              <div>
                <p
                  style={{
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--text-primary)',
                  }}
                >
                  {product.name}
                </p>
                <p
                  style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--text-tertiary)',
                  }}
                >
                  {product.category}
                  {product.dimensions &&
                    ` | ${product.dimensions.width} x ${product.dimensions.height} x ${product.dimensions.depth} mm`}
                </p>
              </div>
            </div>
            <span
              style={{
                borderRadius: 'var(--radius-full)',
                backgroundColor: 'var(--color-primary-50)',
                padding: 'var(--space-1) var(--space-3)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--color-primary-700)',
                flexShrink: 0,
              }}
            >
              ${product.basePrice.toFixed(2)}
            </span>
          </div>

          {/* 3D Preview */}
          {modelUrl && (
            <div>
              <SectionHeading title="3D Preview" />
              <Suspense fallback={<LoadingSpinner size="lg" />}>
                <ModelViewer
                  url={modelUrl}
                  color={selectedColor}
                  className="h-[400px]"
                />
              </Suspense>
            </div>
          )}

          {/* Product image fallback (shown when no 3D file, or as additional context) */}
          {product.imageKey && !modelUrl && (
            <div>
              <SectionHeading title="Product Image" />
              <div
                className="overflow-hidden"
                style={{
                  borderRadius: 'var(--radius-lg)',
                  backgroundColor: 'var(--bg-surface-sunken)',
                }}
              >
                <img
                  src={`/product-images/${product.imageKey}`}
                  alt={product.name}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          )}

          {/* No preview placeholder */}
          {!modelUrl && !product.imageKey && (
            <div
              className="flex aspect-video items-center justify-center"
              style={{
                borderRadius: 'var(--radius-lg)',
                backgroundColor: 'var(--bg-surface-sunken)',
              }}
            >
              <span
                style={{
                  fontSize: '4rem',
                  color: 'var(--color-neutral-300)',
                  fontWeight: 'var(--font-weight-light)',
                }}
              >
                3D
              </span>
            </div>
          )}

          {/* Description */}
          <div>
            <SectionHeading title="About This Product" />
            <p
              style={{
                color: 'var(--text-secondary)',
                lineHeight: 'var(--leading-relaxed)',
              }}
            >
              {product.description}
            </p>
          </div>

          {/* Dimensions */}
          {product.dimensions && (
            <div>
              <SectionHeading title="Dimensions" />
              <p
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--text-secondary)',
                }}
              >
                {product.dimensions.width} x {product.dimensions.height} x{' '}
                {product.dimensions.depth} mm
              </p>
            </div>
          )}

          {/* Available Materials */}
          <div>
            <SectionHeading title="Available Materials" />
            <div
              className="flex flex-wrap"
              style={{ gap: 'var(--space-2)' }}
            >
              {product.materials.map((mat) => (
                <span
                  key={mat}
                  style={{
                    borderRadius: 'var(--radius-md)',
                    backgroundColor: 'var(--bg-surface-sunken)',
                    padding: 'var(--space-1) var(--space-3)',
                    fontSize: 'var(--text-sm)',
                    color: 'var(--text-secondary)',
                  }}
                >
                  {mat.toUpperCase()}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Right column -- sticky checkout panel */}
        <div className="quote-sidebar">
          <div className="quote-sidebar-sticky">
            <ProductCheckoutPanel
              productId={product.productId}
              productName={product.name}
              materialIds={product.materials}
              allMaterials={allMaterials ?? []}
              unitPrice={product.basePrice}
              onColorChange={setSelectedColor}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const SectionHeading = ({ title }: { title: string }) => (
  <h3
    style={{
      marginBottom: 'var(--space-4)',
      fontSize: 'var(--text-lg)',
      fontWeight: 'var(--font-weight-semibold)',
      color: 'var(--text-primary)',
    }}
  >
    {title}
  </h3>
);
